import React, { useState, useCallback } from "react";
import { IconButton, Typography } from "@mui/material";
import { ThumbUpOffAlt, ThumbUp, ThumbDownOffAlt, ThumbDown } from "@mui/icons-material";
import { useSession } from "@/hook/useSession";
import { useTheme } from "@mui/material/styles";
import { formatCount } from "@/utils/formatUtil";
import { useLocale } from "next-intl";

interface LikeDislikeProps {
  lang: string;
  id: string;
  initialLikeCount: number;
  initialDislikeCount: number;
  initialLiked?: boolean;
  initialDisliked?: boolean;
  fontSize?: number;
  onLike: (lang: string, id: string, state: { isLiked: boolean; isDisliked: boolean }) => void;
  onDislike: (lang: string, id: string, state: { isLiked: boolean; isDisliked: boolean }) => void;
}

const labels: Record<string, { like: string; dislike: string }> = {
  ko: { like: "좋아요", dislike: "싫어요" },
  en: { like: "Like", dislike: "Dislike" },
};

export default function LikeDislikeButton({
  lang,
  id,
  initialLikeCount = 0,
  initialDislikeCount = 0,
  initialLiked = false,
  initialDisliked = false,
  fontSize = 20,
  onLike,
  onDislike,
}: LikeDislikeProps) {
  const theme = useTheme();
  const { session } = useSession();
  const locale = useLocale();
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [dislikeCount, setDislikeCount] = useState(initialDislikeCount);
  const [liked, setLiked] = useState(initialLiked && !initialDisliked);
  const [disliked, setDisliked] = useState(initialDisliked && !initialLiked);
  const [lastLikeClicked, setLastLikeClicked] = useState(0);
  const [lastDisLikeClicked, setLastDisLikeClicked] = useState(0);

  const { like: likeLabel, dislike: dislikeLabel } = labels[lang] || labels.en;

  // 좋아요 처리
  const handleLike = useCallback(() => {
    if (!session) return;

    const now = Date.now();
    if (now - lastLikeClicked < 1500) return; // 1.5초 디바운스
    setLastLikeClicked(now);

    const newLiked = !liked;
    const newDisliked = false; // 좋아요 클릭 시 싫어요 해제
    let newLikeCount = likeCount;
    let newDislikeCount = dislikeCount;

    if (newLiked) {
      newLikeCount += 1; // 좋아요 추가
      if (disliked) newDislikeCount -= 1; // 싫어요가 있었다면 제거
    } else {
      newLikeCount -= 1; // 좋아요 취소
    }

    // 음수 방지
    newLikeCount = Math.max(0, newLikeCount);
    newDislikeCount = Math.max(0, newDislikeCount);

    setLiked(newLiked);
    setDisliked(newDisliked);
    setLikeCount(newLikeCount);
    setDislikeCount(newDislikeCount);

    onLike(lang, id, { isLiked: newLiked, isDisliked: newDisliked });
  }, [session, liked, disliked, likeCount, dislikeCount, lang, id, onLike]);

  // 싫어요 처리
  const handleDislike = useCallback(() => {
    if (!session) return;

    const now = Date.now();
    if (now - lastDisLikeClicked < 1500) return; // 1.5초 디바운스
    setLastDisLikeClicked(now);

    const newDisliked = !disliked;
    const newLiked = false; // 싫어요 클릭 시 좋아요 해제
    let newLikeCount = likeCount;
    let newDislikeCount = dislikeCount;

    if (newDisliked) {
      newDislikeCount += 1; // 싫어요 추가
      if (liked) newLikeCount -= 1; // 좋아요가 있었다면 제거
    } else {
      newDislikeCount -= 1; // 싫어요 취소
    }

    // 음수 방지
    newLikeCount = Math.max(0, newLikeCount);
    newDislikeCount = Math.max(0, newDislikeCount);

    setLiked(newLiked);
    setDisliked(newDisliked);
    setLikeCount(newLikeCount);
    setDislikeCount(newDislikeCount);

    onDislike(lang, id, { isLiked: newLiked, isDisliked: newDisliked });
  }, [session, liked, disliked, likeCount, dislikeCount, lang, id, onDislike]);

  const disabled = !session;

  return (
    <>
      <IconButton
        disabled={disabled}
        size="small"
        aria-label={likeLabel}
        onClick={handleLike}
        sx={{ borderRadius: "8px", px: 0.8, py: 0.5 }}
      >
        {liked ? (
          <ThumbUp sx={{ fontSize, color: theme.palette.primary.main }} />
        ) : (
          <ThumbUpOffAlt sx={{ fontSize, color: theme.palette.text.secondary }} />
        )}
        <Typography variant="caption" sx={{ ml: 0.5, color: theme.palette.text.secondary }}>
          {likeCount === 0 ? "" : formatCount(likeCount, locale)}
        </Typography>
      </IconButton>
      <IconButton disabled={disabled} size="small" aria-label={dislikeLabel} onClick={handleDislike}>
        {disliked ? (
          <ThumbDown sx={{ fontSize, color: theme.palette.primary.main }} />
        ) : (
          <ThumbDownOffAlt sx={{ fontSize, color: theme.palette.text.secondary }} />
        )}
      </IconButton>
    </>
  );
}
